import type { StockKlinePoint } from '@/types/stockPool'

/** 涨色 / 跌色（A 股习惯：红涨绿跌） */
export const UP_COLOR = '#ef4444'
export const DOWN_COLOR = '#22c55e'

function fmtSigned(n: number, digits = 2): string {
    const v = n.toFixed(digits)
    return n > 0 ? `+${v}` : v
}

/**
 * 构造 echarts 蜡烛图（candlestick）+ 成交量柱图 option。
 *
 * - 主图：日 K 蜡烛图，红涨绿跌（相对开收）
 * - 副图：成交量柱状图（涨跌同色），与主图共享 X 轴
 * - tooltip 涨跌幅优先用接口 change_pct（相对昨收）
 * - 传入数据需已按日期升序排列
 *
 * echarts candlestick 单根数据格式为 [open, close, low, high]。
 */
export function buildCandlestickOption(points: StockKlinePoint[]) {
    const dates = points.map(p => p.date)

    let prevClose = points[0]?.close ?? 0
    const candleData: [number, number, number, number][] = []
    const volumeData: { value: number; itemStyle: { color: string } }[] = []

    for (const p of points) {
        const open = p.open ?? p.close
        const high = p.high ?? Math.max(open, p.close)
        const low = p.low ?? Math.min(open, p.close)
        candleData.push([open, p.close, low, high])

        // 柱体涨跌按开收；无开盘价时退回相对昨收
        const up = p.close >= (p.open ?? prevClose)
        volumeData.push({
            value: p.volume ?? 0,
            itemStyle: { color: up ? `${UP_COLOR}88` : `${DOWN_COLOR}88` },
        })
        prevClose = p.close
    }

    // Y 轴范围（含影线高低），留 5% 余量
    const allHighs = points.map(p => p.high ?? p.close)
    const allLows = points.map(p => p.low ?? p.close)
    const maxPrice = Math.max(...allHighs)
    const minPrice = Math.min(...allLows)
    const pad = (maxPrice - minPrice) * 0.05 || maxPrice * 0.02

    // X 轴标签稀疏化：约 6 个刻度
    const tickStep = Math.max(1, Math.floor(dates.length / 6))

    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' },
            formatter: (params: any[]) => {
                const candle = params.find((p: any) => p.seriesName === '日K')
                const vol = params.find((p: any) => p.seriesName === '成交量')
                if (!candle) return ''
                const idx = candle.dataIndex as number
                const point = points[idx]
                const [open, close, low, high] = (candle.value as number[]).map((v: number) => v?.toFixed(2))
                const chgPct = point?.change_pct
                const chg = point?.change
                const bodyChg = Number(close) - Number(open)
                const displayChg = chg != null ? chg : bodyChg
                const displayPct = chgPct != null
                    ? chgPct
                    : (Number(open) ? bodyChg / Number(open) * 100 : 0)
                const color = displayPct >= 0 ? UP_COLOR : DOWN_COLOR
                return `<div style="font-size:12px;line-height:1.9">
                    <b>${candle.name}</b><br/>
                    <span>开 ${open}</span>　<span>高 ${high}</span><br/>
                    <span>低 ${low}</span>　<span>收 <b style="color:${color}">${close}</b></span><br/>
                    <span style="color:${color}">${fmtSigned(displayChg)} (${fmtSigned(displayPct)}%)</span>
                    ${vol?.value ? `<br/>量 ${Number(vol.value).toLocaleString()}` : ''}
                </div>`
            },
        },
        grid: [
            { left: 60, right: 20, top: 16, height: '62%' },
            { left: 60, right: 20, top: '74%', height: '20%' },
        ],
        xAxis: [
            {
                type: 'category',
                data: dates,
                gridIndex: 0,
                axisLabel: {
                    color: '#64748b',
                    fontSize: 10,
                    interval: (index: number) => index % tickStep === 0,
                },
                axisLine: { lineStyle: { color: 'rgba(148,163,184,0.15)' } },
                axisTick: { show: false },
            },
            {
                type: 'category',
                data: dates,
                gridIndex: 1,
                axisLabel: { show: false },
                axisLine: { show: false },
                axisTick: { show: false },
            },
        ],
        yAxis: [
            {
                type: 'value',
                scale: true,
                min: (minPrice - pad).toFixed(2),
                max: (maxPrice + pad).toFixed(2),
                gridIndex: 0,
                axisLabel: { color: '#64748b', fontSize: 10 },
                splitLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
            },
            {
                type: 'value',
                gridIndex: 1,
                axisLabel: { show: false },
                splitLine: { show: false },
            },
        ],
        series: [
            {
                name: '日K',
                type: 'candlestick',
                data: candleData,
                // echarts: color=阳线(收>=开)，color0=阴线 → 红涨绿跌
                itemStyle: {
                    color: UP_COLOR,
                    color0: DOWN_COLOR,
                    borderColor: UP_COLOR,
                    borderColor0: DOWN_COLOR,
                },
            },
            {
                name: '成交量',
                type: 'bar',
                xAxisIndex: 1,
                yAxisIndex: 1,
                data: volumeData,
                barWidth: '60%',
            },
        ],
    }
}
